<script>
import SpellCheckedText from "@/components/SpellCheckedText.vue";
import PhotoBox from "@/components/PhotoBox.vue";

export default {
    name: "CPTemp3",
    components: {
        SpellCheckedText,
        PhotoBox
    },
    props: {
        projects: { type: Object, required: true },
        pageGap: { type: Boolean, default: true },
        startPage: { type: Number, default: 1 },
    },
    computed: {
        projectPages() {
            const pages = [];
            const list = this.projects.projects;

            for (let i = 0; i < list.length; i += 4) {
                pages.push(list.slice(i, i + 4));
            }

            return pages;
        }
    }
};
</script>

<template>
  <div class="main-container" :style="{
    gap: pageGap ? '20px' : '0px',
    '--header-color': projects.style.colorScheme.header,
    '--body-color': projects.style.colorScheme.body,
    '--footer-color': projects.style.colorScheme.footer,
    '--heading-color': projects.style.heading.color,
    '--heading-size': projects.style.heading.size + 'px',
    '--heading-font': projects.style.heading.font,
    '--heading-weight': projects.style.heading.bold ? 'bold' : 'normal',
    '--heading-style': projects.style.heading.italic ? 'italic' : 'normal',
    '--heading-decoration': projects.style.heading.underline
      ? 'underline'
      : 'none',
    '--label-color': projects.style.label.color,
    '--label-size': projects.style.label.size + 'px',
    '--label-font': projects.style.label.font,
    '--label-weight': projects.style.label.bold ? 'bold' : 'normal',
    '--label-style': projects.style.label.italic ? 'italic' : 'normal',
    '--label-decoration': projects.style.label.underline
      ? 'underline'
      : 'none',
    '--text-color': projects.style.text.color,
    '--text-size': projects.style.text.size + 'px',
    '--text-font': projects.style.text.font,
    '--text-weight': projects.style.text.bold ? 'bold' : 'normal',
    '--text-style': projects.style.text.italic ? 'italic' : 'normal',
    '--text-decoration': projects.style.text.underline ? 'underline' : 'none',
  }">
    <div class="sub-container page-unit" v-for="(page, pageIndex) in projectPages" :key="pageIndex">
      <!-- HEADER -->
      <div class="banner header-banner">
        <div class="header-title">
          <SpellCheckedText :value="projects.title" @input="projects.title = $event" />
        </div>
      </div>

      <!-- GRID -->
      <div class="compare-container">
        <div class="comp-grid">
          <!-- REAL PROJECTS -->
          <div class="comp-info" v-for="(comp, index) in page" :key="index">
            <h2 class="name">
              <SpellCheckedText :value="comp.projectName || 'N/A'" @input="comp.projectName = $event" />
            </h2>
            <p class="value mb-2">
              <SpellCheckedText :value="comp.city || 'N/A'" @input="comp.city = $event" />,
              <SpellCheckedText :value="comp.stateProvince || 'N/A'" @input="comp.stateProvince = $event" /> <br />
              <span>
                Size: {{ comp.projectSize ? `${comp.projectSize.toLocaleString()} SF` : 'N/A' }}
              </span>
              <span>|</span>
              <span>
                Value:
                {{ comp.value ? `$${comp.value.toLocaleString()}` : 'N/A' }}
              </span>
              <br />
              <span v-if="comp.constructionCompletionDate">
                Completion: {{ new Date(comp.constructionCompletionDate || 'N/A').getFullYear() }}
              </span>

            </p>
            <div class="project-box">
              <PhotoBox class="photobox" :project="comp" height="200px" />
            </div>

            <div class="my-2 ">
              <p class="value mb-2">
                <span class="label">Client:</span>
                <SpellCheckedText :value="comp.clientAccountName || 'N/A'" @input="comp.clientAccountName = $event" />
              </p>

              <p class="value mb-2">
                <span class="label">Designers:</span>
                <span v-if="!comp.designerAccounts || comp.designerAccounts.length == 0">N/A</span>
                <span v-else v-for="(designer, dIndex) in comp.designerAccounts" :key="dIndex">
                  <SpellCheckedText :value="designer.accountName || 'N/A'" @input="designer.accountName = $event" />
                  <span v-if="dIndex !== comp.designerAccounts.length - 1">,</span>
                </span>
              </p>
            </div>
          </div>

          <!-- FILLER BOXES TO MAKE 4 PER PAGE -->
          <div v-for="n in 4 - page.length" :key="'empty-' + n" class="comp-info"></div>
        </div>
      </div>

      <!-- FOOTER -->
      <div class="banner footer-banner">
        <span class="page-number">{{ String(startPage + pageIndex).padStart(2, '0') }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.main-container {
  display: flex;
  flex-direction: column;
  width: 211mm;
  min-height: 296.83mm;
  position: relative;
  box-sizing: border-box;

  &:hover {
    .photobox :deep(.photo-overlay) {
      opacity: 1;
    }

    .photobox :deep(img) {
      opacity: 0.9;
    }
  }

  .sub-container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background: white;
    height: 296.83mm;

    .banner {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 60px;
      position: relative;

      &.header-banner {
        background: var(--header-color);
      }

      &.footer-banner {
        background: var(--footer-color);
      }

      .header-title {
        font-size: 18px;
        color: white;
        margin-bottom: 0px;
        font-weight: 700;
        font-family: Helvetica;
      }

      .page-number {
        position: absolute;
        right: 15px;
        bottom: 10px;
        font-size: 14px;
        font-weight: 700;
        color: white;
        font-family: Helvetica;
      }
    }

    .compare-container {
      flex: 1;
      background: white;
      padding: 50px 40px;
      overflow: hidden;

      .comp-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 30px 50px;
        align-items: stretch;

        .comp-info {
          display: flex;
          flex-direction: column;
          text-align: left;
          background: var(--body-color);
          padding: 10px;
          min-height: 390px;
          border-radius: 20px;
          color: white;

          .name {
            color: var(--heading-color);
            font-size: var(--heading-size);
            font-family: var(--heading-font);
            font-weight: var(--heading-weight);
            font-style: var(--heading-style);
            text-decoration: var(--heading-decoration);
          }

          .label {
            color: var(--label-color);
            font-size: var(--label-size);
            font-family: var(--label-font);
            font-weight: var(--label-weight);
            font-style: var(--label-style);
            text-decoration: var(--label-decoration);
          }

          .value {
            color: var(--text-color);
            font-size: var(--text-size);
            font-family: var(--text-font);
            font-weight: var(--text-weight);
            font-style: var(--text-style);
            text-decoration: var(--text-decoration);
          }

          .project-box {
            background: white;
            width: 100%;
            height: 200px;
            border-radius: 10px;
            overflow: hidden;
          }

          .imageBox {
            background: white;
            width: 100%;
            height: 200px;
            margin-bottom: 10px;
          }
        }
      }
    }
  }
}
</style>
```